import { ComponentProps, createContext, forwardRef, useContext, useState } from 'react'
import { Icons } from '../..'
import { styled } from '../../config'
import { Box } from '../Box'
import { Card } from './Card'

const CollpasibleCardContext = createContext<{
  isCollapsed: boolean
  isCollapsible?: boolean
  toggleIsCollapsed: () => void
}>({ isCollapsed: false, toggleIsCollapsed: () => {} })

const StyledRoot = styled(Card.Root, {
  padding: '16px'
})

const Header = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  gap: '8px',
  justifyContent: 'space-between',
  alignItems: 'center'
})

const Arrow = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(({ ...props }, ref) => {
  const state = useContext(CollpasibleCardContext)
  return (
    <Box ref={ref} row alignCenter justifyCenter {...props} css={{ paddingLeft: '16px' }}>
      {state.isCollapsible && (
        <Icons.ChevronUp
          style={{
            transform: state.isCollapsed ? 'rotate(180deg)' : undefined,
            transition: '250ms ease'
          }}
        />
      )}
    </Box>
  )
})

const Content = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(
  ({ children, ...props }, ref) => {
    const state = useContext(CollpasibleCardContext)
    return (
      <Box
        column
        ref={ref}
        css={{
          maxHeight: state.isCollapsed ? 0 : '100vh',
          opacity: state.isCollapsed ? '0%' : '100%',
          transition: '250ms ease'
        }}
        {...props}
      >
        {children}
      </Box>
    )
  }
)

const Trigger = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(
  ({ children, ...props }, ref) => {
    const state = useContext(CollpasibleCardContext)
    return (
      <Box
        ref={ref}
        {...props}
        onClick={(e) => {
          if (!state.isCollapsible) return

          state.toggleIsCollapsed()
          props.onClick && props.onClick(e)
        }}
        css={{ cursor: 'pointer' }}
      >
        {children}
      </Box>
    )
  }
)

interface RootProps {
  isCollapsed?: boolean
  isCollapsible?: boolean
}
export const Root = forwardRef<HTMLDivElement, RootProps & ComponentProps<typeof Card.Root>>(
  ({ isCollapsed, isCollapsible, children, ...props }, ref) => {
    const [collapsed, setCollapsed] = useState<boolean>(isCollapsed === true)
    return (
      <StyledRoot ref={ref} {...props}>
        <CollpasibleCardContext.Provider
          value={{
            isCollapsed: collapsed,
            isCollapsible,
            toggleIsCollapsed: () => setCollapsed(!collapsed)
          }}
        >
          {children}
        </CollpasibleCardContext.Provider>
      </StyledRoot>
    )
  }
)

/**
 * Example CollapsibleCard Element:
 *
 * <CollapsibleCard.Root>
 *  <CollapsibleCard.Trigger>
 *   <CollapsibleCard.Header>
 *     <CollapsibleCard.Text type="title">Dropdown!</CollapsibleCard.Text>
 *     <CollapsibleCard.Arrow />
 *   </CollapsibleCard.Header>
 *  </CollapsibleCard.Trigger>
 *  <CollapsibleCard.Content>
 *    <Box>I'm hidden until clicked</Box>
 *  </CollapsibleCard.Content>
 * </CollapsibleCard.Root>
 */
export const CollapsibleCard = {
  ...Card,
  Root,
  Arrow,
  Trigger,
  Header,
  Content
}
